'use client';

import { useCallback, useEffect, useState } from 'react';
import { useHits } from 'react-instantsearch';

import AlgoliaNoResultsBoundary from '@app/plp/components/AlgoliaNoResultsBoundary';
import ProductGrid from '@app/plp/components/ProductGrid/ProductGrid';
import { transformHitForProductTile, type ProductHit } from '@libs/algolia';
import type { Product } from '@libs/common-next';
import type { ProductListProps } from './types';

export default function ProductList({
    entryId,
    forceCategoryH1,
    seoMetadata,
    isCategoryActive,
}: ProductListProps) {
    const [stringifiedProductIds, setStringifiedProductIds] =
        useState<string>('');
    const [productsToShow, setProductsToShow] = useState<Product[]>([]);
    const [queryId, setQueryId] = useState<string>('first_query');

    const transformItems = useCallback(
        (items: ProductHit[]) =>
            items.map((item: ProductHit) => transformHitForProductTile(item)),
        []
    );

    const stringifiyIds = (items: ProductHit[]) =>
        items.map((p: ProductHit) => p.id).join(',');

    const { items, results } = useHits({ transformItems });

    const products = (
        stringifiedProductIds !== '' ? productsToShow : items
    ) as Product[];

    // This is a hack to only update hits to be displayed if they have ACTUALLY changed. When filters change in InstantSearch the Algolia hook first returns the old list
    // then it updates with the new list a few seconds later, seems like a bug with Algolia
    useEffect(() => {
        const newStringifiedIds = stringifiyIds(items);
        if (newStringifiedIds !== stringifiedProductIds) {
            setStringifiedProductIds(newStringifiedIds);
            setProductsToShow(items as Product[]);
            setQueryId(results?.queryID ?? '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Only need to run when hits change
    }, [items]);

    return (
        <AlgoliaNoResultsBoundary
            products={products}
            isCategoryActive={isCategoryActive}
        >
            <ProductGrid
                entryId={entryId}
                products={products}
                totalResults={results ? results.nbHits : 0}
                queryid={queryId}
                forceCategoryH1={forceCategoryH1}
                seoMetadata={seoMetadata}
            />
        </AlgoliaNoResultsBoundary>
    );
}
